import { getConfigValue } from "../config/runtimeConfig";

const API_BASE = getConfigValue(
  "REACT_APP_API_PAYMENTS_URL",
  process.env.REACT_APP_API_PAYMENTS_URL
);

let authToken = null;
let authTokenProvider = null;

export const setPaymentsApiToken = (token) => {
  authToken = token || null;
};

export const setPaymentsApiTokenProvider = (provider) => {
  authTokenProvider = typeof provider === "function" ? provider : null;
};

const resolveAuthToken = async () => {
  if (authTokenProvider) {
    try {
      return await authTokenProvider();
    } catch (error) {
      return null;
    }
  }
  return authToken;
};

const buildUrl = (path) => `${API_BASE}${path}`;

const isJsonResponse = (response) => {
  const contentType = response.headers.get("content-type") || "";
  return contentType.includes("application/json");
};

const parseResponseBody = async (response) => {
  if (isJsonResponse(response)) {
    return response.json();
  }
  return response.text();
};

const request = async (path, options = {}) => {
  if (!API_BASE) {
    throw new Error("REACT_APP_API_PAYMENTS_URL is not set.");
  }
  const token = await resolveAuthToken();
  if (options.requireAuth && !token) {
    throw new Error("Authentication token is missing. Please sign in again before continuing.");
  }
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };
  if (token && !headers.Authorization && !headers.authorization) {
    headers.Authorization = `Bearer ${token}`;
  }
  const config = {
    ...options,
    headers,
  };
  delete config.requireAuth;

  const response = await fetch(buildUrl(path), config);
  const data = await parseResponseBody(response);

  if (!response.ok) {
    const message =
      (typeof data === "string" && data.trim()) ||
      data?.message ||
      data?.error ||
      data?.errorMessage ||
      data?.title ||
      data?.detail ||
      data?.details ||
      data?.error?.message ||
      response.statusText ||
      "Request failed.";
    let details = "";
    if (typeof data === "string") {
      details = data;
    } else if (data && typeof data === "object") {
      details = JSON.stringify(data, null, 2);
    }
    if (!details && response.statusText) {
      details = response.statusText;
    }
    const error = new Error(message);
    if (details) {
      error.details = details;
    }
    throw error;
  }

  return data;
};

export const createInvoice = (payload) =>
  request("/api/invoices", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const getInvoices = () => request("/api/invoices/all");

export const getPaymentsHistory = () => request("/api/payments/history");

export const refundPayment = (paymentId) =>
  request(`/api/payments/${paymentId}/refund`, {
    method: "POST",
  });

export const getAnalyticsOverview = (payload = {}) =>
  request("/api/analytics/overview", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const getDisputes = () => request("/api/payments/disputes");

export const submitDisputeEvidence = (payload) =>
  request("/api/payments/disputes/submit", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const createCheckoutSession = (payload) =>
  request("/api/payments/checkout", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const createSubscriptionCheckoutSession = (payload) =>
  request("/api/subscriptions/checkout", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const createClientSubscriptionCheckoutSession = (payload) =>
  request("/api/client/subscriptions/checkout", {
    method: "POST",
    requireAuth: true,
    body: JSON.stringify(payload),
  });

export const createPlan = (payload) =>
  request("/api/subscriptions/plans", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const getPlans = () => request("/api/subscriptions/plans");

export const deletePlan = (planId) =>
  request(`/api/subscriptions/plans/${planId}`, {
    method: "DELETE",
  });

export const createSubscription = (payload) =>
  request("/api/subscriptions", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const getSubscriptions = () => request("/api/subscriptions");

export const getSubscriptionInvoices = () => request("/api/subscriptions/invoices");

export const cancelSubscription = (subscriptionId) =>
  request(`/api/subscriptions/${subscriptionId}/cancel`, {
    method: "POST",
  });

export const getClientSubscriptions = () => request("/api/client/subscriptions", { requireAuth: true });

export const getClientSubscriptionInvoices = () =>
  request("/api/client/subscriptions/invoices", { requireAuth: true });

export const cancelClientSubscription = (subscriptionId) =>
  request(`/api/client/subscriptions/${subscriptionId}/cancel`, {
    method: "POST",
    requireAuth: true,
  });

